import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getPermissionDescription } from "../../../constants/permissionDescriptions";
import {
  filterRolePermissionTreeGroups,
  PERMISSION_TREE_MODULE_META,
} from "../../../utils/rolePermissionTree";
import "./RolePermissionTree.scss";

const TOOLTIP_MAX_WIDTH = 224;
const TOOLTIP_ESTIMATED_HEIGHT = 72;
const VIEWPORT_PADDING = 8;

function getGroupKeys(group) {
  return (group.permissions || []).map((item) => item.key).filter(Boolean);
}

function getGroupIconMeta(group) {
  const fromMeta = PERMISSION_TREE_MODULE_META[group?.id];
  return {
    icon: group?.icon || fromMeta?.icon || "fa-cube",
    color: group?.color || fromMeta?.color || "#64748b",
  };
}

export function PermissionInfoIcon({ permission }) {
  const description = getPermissionDescription(
    permission.key,
    permission.label,
  );
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({
    top: 0,
    left: 0,
    maxWidth: TOOLTIP_MAX_WIDTH,
    transform: "translateY(-100%)",
  });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tooltipHeight =
      tooltipRef.current?.offsetHeight || TOOLTIP_ESTIMATED_HEIGHT;
    const tooltipWidth = Math.min(
      TOOLTIP_MAX_WIDTH,
      window.innerWidth - VIEWPORT_PADDING * 2,
    );

    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const showAbove =
      spaceAbove >= tooltipHeight + 6 ||
      spaceAbove >= spaceBelow;

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - tooltipWidth - VIEWPORT_PADDING),
    );

    const top = showAbove
      ? rect.top - 6
      : rect.bottom + 6;

    setStyle({
      top,
      left,
      maxWidth: tooltipWidth,
      transform: showAbove ? "translateY(-100%)" : "translateY(0)",
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition, description]);

  useEffect(() => {
    if (open && tooltipRef.current) {
      updatePosition();
    }
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="role-permission-tree__info"
        tabIndex={0}
        aria-label={description}
        onMouseEnter={() => {
          setOpen(true);
          requestAnimationFrame(updatePosition);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          setOpen(true);
          requestAnimationFrame(updatePosition);
        }}
        onBlur={() => setOpen(false)}
        onClick={(event) => event.stopPropagation()}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </span>
      {open
        ? createPortal(
            <span
              ref={tooltipRef}
              className="role-permission-tree__tooltip role-permission-tree__tooltip--portal"
              role="tooltip"
              style={style}
            >
              {description}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

function RolePermissionTree({
  groups = [],
  selectedPermissions = [],
  onTogglePermission,
  onSelectMany,
  onDeselectMany,
  canEdit = true,
  showAllSelected = false,
  embedded = false,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState("");

  const filteredGroups = useMemo(
    () => filterRolePermissionTreeGroups(groups, searchTerm),
    [groups, searchTerm],
  );

  // Default collapsed. While searching, open matching groups so results are visible.
  useEffect(() => {
    const q = String(searchTerm || "").trim();
    if (!q) {
      setExpanded(new Set());
      return;
    }
    setExpanded(new Set(filteredGroups.map((group) => group.id)));
  }, [searchTerm, filteredGroups]);

  const isChecked = (key) =>
    showAllSelected || selectedPermissions.includes(key);

  const getGroupState = (group) => {
    const keys = getGroupKeys(group);
    if (!keys.length) return "none";
    const selectedCount = keys.filter((key) => isChecked(key)).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === keys.length) return "all";
    return "some";
  };

  const toggleGroup = (group) => {
    if (!canEdit || showAllSelected) return;
    const keys = getGroupKeys(group);
    const state = getGroupState(group);
    if (state === "all") onDeselectMany?.(keys);
    else onSelectMany?.(keys);
  };

  const toggleExpanded = (groupId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (!groups.length) {
    return (
      <div
        className={`role-permission-tree role-permission-tree--empty${
          embedded ? " role-permission-tree--embedded" : ""
        }`}
      >
        No permissions available.
      </div>
    );
  }

  return (
    <div
      className={`role-permission-tree${
        embedded ? " role-permission-tree--embedded" : ""
      }`}
    >
      <div className="role-permission-tree__toolbar">
        <input
          type="search"
          className="role-permission-tree__search"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      <div className="role-permission-tree__body" role="tree">
        {filteredGroups.map((group) => {
          const isOpen = expanded.has(group.id);
          const groupState = getGroupState(group);
          const permissionCount = (group.permissions || []).length;
          const { icon: groupIcon, color: groupColor } = getGroupIconMeta(group);

          return (
            <div
              key={group.id}
              className="role-permission-tree__group"
              role="treeitem"
              aria-expanded={isOpen}
            >
              <div className="role-permission-tree__row role-permission-tree__row--group">
                <button
                  type="button"
                  className="role-permission-tree__expander"
                  onClick={() => toggleExpanded(group.id)}
                  aria-label={isOpen ? "Collapse module" : "Expand module"}
                >
                  <span className="role-permission-tree__expander-bracket">[</span>
                  <span className="role-permission-tree__expander-sign">
                    {isOpen ? "−" : "+"}
                  </span>
                  <span className="role-permission-tree__expander-bracket">]</span>
                </button>
                <input
                  type="checkbox"
                  className="role-permission-tree__checkbox"
                  checked={groupState === "all"}
                  ref={(element) => {
                    if (element) element.indeterminate = groupState === "some";
                  }}
                  disabled={!canEdit || showAllSelected}
                  onChange={() => toggleGroup(group)}
                  onClick={(event) => event.stopPropagation()}
                />
                <button
                  type="button"
                  className="role-permission-tree__group-toggle"
                  onClick={() => toggleExpanded(group.id)}
                  aria-expanded={isOpen}
                >
                  <span className="role-permission-tree__count">
                    {permissionCount}
                  </span>
                  <span
                    className="role-permission-tree__icon role-permission-tree__icon--module"
                    style={{ color: groupColor }}
                    aria-hidden="true"
                    title={group.label}
                  >
                    <i className={`fas ${groupIcon}`} />
                  </span>
                  <span
                    className="role-permission-tree__label role-permission-tree__label--module"
                    style={{ color: groupColor }}
                  >
                    {group.label}
                  </span>
                </button>
              </div>

              {isOpen
                ? (group.permissions || []).map((permission) => (
                    <div
                      key={permission.key}
                      className="role-permission-tree__row role-permission-tree__row--leaf"
                      role="treeitem"
                    >
                      <span className="role-permission-tree__expander role-permission-tree__expander--spacer" />
                      <input
                        type="checkbox"
                        className="role-permission-tree__checkbox"
                        checked={isChecked(permission.key)}
                        disabled={!canEdit || showAllSelected}
                        onChange={() => onTogglePermission?.(permission.key)}
                      />
                      <span
                        className="role-permission-tree__icon"
                        aria-hidden="true"
                      >
                        <i className="fas fa-key" />
                      </span>
                      <span className="role-permission-tree__label">
                        {permission.label}
                      </span>
                      <PermissionInfoIcon permission={permission} />
                    </div>
                  ))
                : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RolePermissionTree;
